import localforage from 'localforage';
import type { Settings } from '../types';

const store = localforage.createInstance({ name: 'ai-cong-no', storeName: 'settings' });
const KEY = 'app-settings';
const SETTINGS_SCHEMA_VERSION = 8;
const MEINVOICE_CONFIG_API_URL = 'https://api-tiengtrungthanhhang.19t.vn/api/public/misa-meinvoice-settings/current';
const MISA_CONFIG_API_SECRET = '123456789';

let remoteMeInvoiceConfigPromise: Promise<string> | undefined;

function fetchRemoteMeInvoiceConfigJson(): Promise<string> {
  remoteMeInvoiceConfigPromise ??= fetch(MEINVOICE_CONFIG_API_URL, {
    headers: { Accept: 'application/json', 'X-MISA-Config-Secret': MISA_CONFIG_API_SECRET },
    cache: 'no-store',
  }).then(async (response) => {
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = await response.json() as { data?: Record<string, unknown>; config_json?: string };
    const raw = body.config_json || (body.data as any)?.config_json;
    const config = raw ? JSON.parse(raw) : body.data;
    if (!config?.baseUrl || !config.authorization || !(config.clientId || config.clientid) ||
      !(config.subscriberCode || config.subscribercode) || !config.subscriberId || !(config.organizationId || config.orgId)) {
      throw new Error('Dữ liệu cấu hình meInvoice từ server không hợp lệ.');
    }
    return JSON.stringify(config, null, 2);
  });
  return remoteMeInvoiceConfigPromise;
}

interface StoredSettings extends Partial<Settings> {
  settingsSchemaVersion?: number;
}

export const defaultSettings: Settings = {
  provider: 'anthropic',
  claudeApiKey: 'skA6fQp-ul9V5Fk0AlZiPKHA3cmPJAJU4ykqUDVnhD6Ws1Ck',
  baseUrl: 'https://viber.19t.vn/',
  authScheme: 'x-api-key',
  model: 'claude-sonnet-5',
  payerCompanyName: '',
  matchAmountTolerancePct: 1,
  matchAmountToleranceAbs: 10000,
  matchDateWindowDays: 30,
  misaConfigJson: '',
  meInvoiceConfigJson: '',
  automationEnabled: true,
  aiConfidenceThreshold: 0.6,
  autoMatchThreshold: 0.6,
  autoMatchGap: 0.15,
  bankMappingConfidenceThreshold: 0.6,
  misaDetailConcurrency: 4,
  bankAliases: {},
};

export function upgradeStoredSettings(stored?: StoredSettings | null): Settings {
  const { settingsSchemaVersion = 0, ...values } = stored || {};
  const upgraded = { ...defaultSettings, ...values };

  if (settingsSchemaVersion < SETTINGS_SCHEMA_VERSION) {
    if (values.aiConfidenceThreshold === 0.85) upgraded.aiConfidenceThreshold = 0.6;
    if (values.autoMatchThreshold === 0.85) upgraded.autoMatchThreshold = 0.6;
    if (values.bankMappingConfidenceThreshold === 0.95) upgraded.bankMappingConfidenceThreshold = 0.6;
  }

  if (settingsSchemaVersion < 2) {
    const usesLegacyAiDefaults =
      (values.provider == null || values.provider === 'anthropic') &&
      (values.claudeApiKey == null || values.claudeApiKey === '') &&
      (values.baseUrl == null || values.baseUrl === '') &&
      (values.authScheme == null || values.authScheme === 'x-api-key') &&
      (values.model == null || values.model === 'claude-sonnet-5');

    if (usesLegacyAiDefaults) {
      upgraded.provider = defaultSettings.provider;
      upgraded.claudeApiKey = defaultSettings.claudeApiKey;
      upgraded.baseUrl = defaultSettings.baseUrl;
      upgraded.authScheme = defaultSettings.authScheme;
      upgraded.model = defaultSettings.model;
    }

    if (values.misaConfigJson == null || values.misaConfigJson === '') {
      upgraded.misaConfigJson = defaultSettings.misaConfigJson;
    }
  }

  if (
    settingsSchemaVersion < 3 &&
    values.provider === 'openai' &&
    values.authScheme === 'bearer' &&
    values.baseUrl === 'https://api-gpt.19t.vn' &&
    values.model === 'gpt-5.4-mini'
  ) {
    upgraded.model = defaultSettings.model;
  }

  if (settingsSchemaVersion < 4) {
    const usesDefaultOpenAiGateway =
      values.provider === 'openai' &&
      values.authScheme === 'bearer' &&
      values.baseUrl === 'https://api-gpt.19t.vn' &&
      values.model === 'gpt-5.6-luna';

    if (usesDefaultOpenAiGateway && (values.claudeApiKey == null || values.claudeApiKey === '')) {
      upgraded.claudeApiKey = defaultSettings.claudeApiKey;
    }

    if (values.misaConfigJson == null || values.misaConfigJson === '') {
      upgraded.misaConfigJson = defaultSettings.misaConfigJson;
    }
  }

  if (settingsSchemaVersion < 7) {
    const usesPreviousDefaultGateway =
      values.provider === 'openai' &&
      values.authScheme === 'bearer' &&
      values.baseUrl === 'https://api-gpt.19t.vn' &&
      (values.model === 'gpt-5.6-luna' || values.model === 'gpt-5.4-mini') &&
      (values.claudeApiKey == null || values.claudeApiKey === '' ||
        values.claudeApiKey === 'agt_codex_lTP3rKPrEDsteXgzazWuGB80TO5CVxmV');

    if (usesPreviousDefaultGateway) {
      upgraded.baseUrl = defaultSettings.baseUrl;
      upgraded.claudeApiKey = defaultSettings.claudeApiKey;
    }
  }

  if (settingsSchemaVersion < 8) {
    const usesClaudeCredentialsAsOpenAi =
      values.provider === 'openai' &&
      values.authScheme === 'bearer' &&
      (values.model === 'gpt-5.6-luna' || values.model === 'gpt-5.4-mini') &&
      (
        (values.baseUrl === 'https://viber.19t.vn/' &&
          values.claudeApiKey === 'skA6fQp-ul9V5Fk0AlZiPKHA3cmPJAJU4ykqUDVnhD6Ws1Ck') ||
        (values.baseUrl === 'https://api-gpt.19t.vn' &&
          (values.claudeApiKey == null || values.claudeApiKey === '' ||
            values.claudeApiKey === 'agt_codex_lTP3rKPrEDsteXgzazWuGB80TO5CVxmV'))
      );

    if (usesClaudeCredentialsAsOpenAi) {
      upgraded.provider = defaultSettings.provider;
      upgraded.baseUrl = defaultSettings.baseUrl;
      upgraded.claudeApiKey = defaultSettings.claudeApiKey;
      upgraded.authScheme = defaultSettings.authScheme;
      upgraded.model = defaultSettings.model;
    }
  }

  return upgraded;
}

export async function getSettings(): Promise<Settings> {
  const stored = await store.getItem<StoredSettings>(KEY);
  let settings = upgradeStoredSettings(stored);
  let shouldPersist = Boolean(stored && (stored.settingsSchemaVersion || 0) < SETTINGS_SCHEMA_VERSION);

  try {
    const meInvoiceConfigJson = await fetchRemoteMeInvoiceConfigJson();
    if (settings.meInvoiceConfigJson !== meInvoiceConfigJson) {
      settings = { ...settings, meInvoiceConfigJson };
      shouldPersist = true;
    }
  } catch (error) {
    console.warn('Không thể đồng bộ cấu hình meInvoice từ server; sử dụng cấu hình đã lưu cục bộ.', error);
  }

  if (shouldPersist) {
    await store.setItem(KEY, { ...settings, settingsSchemaVersion: SETTINGS_SCHEMA_VERSION });
  }
  return settings;
}

export async function saveSettings(s: Settings): Promise<void> {
  await store.setItem(KEY, { ...s, settingsSchemaVersion: SETTINGS_SCHEMA_VERSION });
}
